import {
  createConversationSession,
  fetchChatbotMetadata,
  reportError,
  sendMessage as streamMessage,
  streamGreeting,
  type ChatStreamHandlers,
} from "@noa/api-client";
import {
  ROLE_VALUES,
  type ChatbotState,
  type ConversationMessageApiModel as ChatMessage,
  type UsedSource,
} from "@noa/api-core";
import { i18next } from "@noa/i18n";
import { useAppStore } from "./app.store";
import { excerptReasoning, useBotStore } from "./bot.store";

// ---------------------------------------------------------------------------
// SSE events → store writes
// ---------------------------------------------------------------------------

/**
 * A chunk starts a NEW assistant bubble when:
 * 1. no messages exist yet,
 * 2. the last message is the user's, or
 * 3. the last assistant message already has an id (its bubble is complete — the id arrives on a
 *    separate SSE event after the final chunk).
 *
 * This decision needs the message list, which is why it lives here and not in `@noa/api-client`.
 */
const handleChunk = (chunkContent: string) => {
  const { addToLastMessage, messages, addMessage, getLatestMessage } = useAppStore.getState();

  const lastMessage = messages.length > 0 ? getLatestMessage() : null;
  const needsNewMessage = !lastMessage || lastMessage.role !== ROLE_VALUES.ASSISTANT || lastMessage.message_id !== "";

  if (needsNewMessage) {
    addMessage({
      role: ROLE_VALUES.ASSISTANT,
      content: chunkContent,
      message_id: "",
      created_at: new Date().toISOString(),
      used_sources: [],
      feedback: 0,
    });
  } else {
    addToLastMessage(chunkContent);
  }
};

/**
 * What one live stream has produced that the store would be holding if its conversation were on
 * screen. Kept out here because the store holds a single conversation and `resetStore()` wipes it,
 * while a stream deliberately outlives its conversation being open — so leaving mid-reply would
 * otherwise destroy the only copy of an unfinished reply (the server persists it only once the stream
 * ends) and leave the status describing whatever stream spoke last.
 *
 * One entry per stream, created with it and deleted when it settles (`runStream`), so the map can't
 * outgrow the live streams. `loadConversation` reads it back through `getLiveAssistantBubble` and
 * `restoreLiveStreamStatus`, which is what makes re-entering mid-reply show the bubble whole — with
 * the sources it had already cited — under the status the stream is actually at.
 *
 * The fields have deliberately different lifetimes, which is why each is cleared on its own event
 * rather than the record being dropped: a bubble ends at its `message_id`, the description restarts
 * there and at every reset point, and state keeps arriving after it.
 */
type LiveStream = {
  /** The open bubble: text and sources accumulated since the last `message_id`. */
  content: string;
  usedSources: UsedSource[];
  /** The last state reported, or null while none has been. */
  state: ChatbotState | null;
  /**
   * The action description as the store would hold it, or null while nothing has set it since the
   * last reset — the case where a restore must leave whatever the state itself implies (see
   * `restoreLiveStreamStatus`). Reduced with the store's own `excerptReasoning`, so the rules that
   * shape a description are not mirrored here.
   */
  description: string | null;
};

const liveStreams = new Map<string, LiveStream>();

/**
 * The record for `sessionId`'s stream. Created with the handlers, which then hold on to it, so an
 * event arriving after the stream settled writes to a detached object instead of resurrecting an entry
 * nothing will ever delete.
 */
const liveStream = (sessionId: string): LiveStream => {
  const existing = liveStreams.get(sessionId);
  if (existing) return existing;

  const created: LiveStream = { content: "", usedSources: [], state: null, description: null };
  liveStreams.set(sessionId, created);
  return created;
};

/** The open bubble on `sessionId`'s stream, as a message, or null if it has none. */
export const getLiveAssistantBubble = (sessionId: string): ChatMessage | null => {
  const live = liveStreams.get(sessionId);
  // No text means nothing to show: a bubble holding only sources would render empty.
  if (!live?.content) return null;

  return {
    role: ROLE_VALUES.ASSISTANT,
    content: live.content,
    // Empty, like any streaming bubble: it marks the bubble unfinished, so the next chunk appends to
    // it rather than opening another one.
    message_id: "",
    created_at: new Date().toISOString(),
    // Copied, so later `addUsedSourceData` calls mutate the store's array and not the buffer's.
    used_sources: [...live.usedSources],
    feedback: 0,
  };
};

const handleMessageId = (messageId: string) => {
  const appState = useAppStore.getState();
  appState.addMessageId(messageId);
  appState.setSelectedMessageId(messageId);

  useBotStore.getState().saveActionDescriptionForMessage(messageId);
};

/**
 * The server generated a name for this conversation. Parked in the store, not written straight into
 * the conversation-list cache — that write would be lost: `updateConversationRow` no-ops on an
 * uncached row, the title arrives before the list refetch has produced that row, and nothing retries.
 * Held here it is durable, so `@noa/ui` can project it onto the row whenever the row appears
 * (`useSyncConversationsHistory`, as for the message count) and the header can read it at once
 * (`useActiveConversationTitle` tier 1).
 */
const handleConversationTitle = (title: string) => {
  useAppStore.getState().setConversationName(title);
};

/**
 * Catch the status up to where `sessionId`'s stream is — the state tag and the action description
 * below it — for a conversation being re-entered mid-reply. A no-op when it has no live stream, which
 * is the ordinary case.
 *
 * The state goes through the same `handleState` an arriving event does, so the text a state implies
 * is derived in one place only — and it goes *first*, because it resets the description for every
 * state but `retrieving_sources`. The recorded description then applies on top, exactly as a live
 * event would have; `null` means nothing has set one since the last reset, so what `handleState` just
 * implied is the whole status.
 */
export const restoreLiveStreamStatus = (sessionId: string): void => {
  const live = liveStreams.get(sessionId);
  if (!live) return;

  if (live.state) handleState(live.state);
  if (live.description === null) return;

  const botStore = useBotStore.getState();
  botStore.startActionDescription();
  botStore.appendActionDescription(live.description);
};

/** Maps a bot state to the user-facing status text — needs `@noa/i18n`, so it belongs here. */
const handleState = (state: ChatbotState) => {
  const botStore = useBotStore.getState();

  if (state !== "retrieving_sources") {
    botStore.clearActionDescription();
  }

  botStore.setChatbotState(state);

  if (state === "sending_message") {
    botStore.startActionDescription();
    botStore.appendActionDescription(i18next.t("chat:status.sendingMessage"));
  } else if (state === "generating_query") {
    botStore.startActionDescription();
    botStore.appendActionDescription(i18next.t("chat:status.generatingQuery"));
  }
};

/**
 * The store-backed implementation of `@noa/api-client`'s `ChatStreamHandlers`, **scoped to the
 * session it was created for**. The store holds one conversation at a time; streams don't. Leaving a
 * conversation before its reply finishes resets the store and starts a new session, while the old
 * stream stays open — deliberately, since the server is still persisting that reply, which is what
 * makes it complete when you come back. Unscoped, its remaining chunks wrote into whatever
 * conversation was open by then, so a reply straddling a navigation kept streaming onto Home.
 *
 * The check is live rather than latched, so returning to the conversation resumes its own stream.
 * Holds no state beyond the id — every handler still reads the store through `getState()`.
 */
const createStoreStreamHandlers = (sessionId: string): ChatStreamHandlers => {
  const live = liveStream(sessionId);

  const forThisSession =
    <Args extends unknown[]>(handler: (...args: Args) => void) =>
    (...args: Args): void => {
      if (useAppStore.getState().sessionId !== sessionId) return;
      handler(...args);
    };

  // Each handler records what the event leaves behind — which is what a re-entry restores — and then
  // applies it to the store, only while this session is the one on screen.
  return {
    onChunk: (chunk: string) => {
      live.content += chunk;
      forThisSession(handleChunk)(chunk);
    },
    onUsedSource: (source: UsedSource) => {
      live.usedSources.push(source);
      forThisSession(() => useAppStore.getState().addUsedSourceData(source))();
    },
    // The bubble is finished and persisted, so the server can serve it from here on — and its
    // description moved onto the message, leaving the live one empty.
    onMessageId: (messageId: string) => {
      live.content = "";
      live.usedSources = [];
      live.description = null;
      forThisSession(handleMessageId)(messageId);
    },
    onConversationTitle: forThisSession(handleConversationTitle),
    onState: (state: ChatbotState) => {
      live.state = state;
      // Every state but this one resets the description, and `handleState` re-derives whatever text it
      // implies, so nothing recorded before it is worth keeping.
      if (state !== "retrieving_sources") live.description = null;
      forThisSession(handleState)(state);
    },
    // Mirrors the store's "only on an empty description" rule, with its own reduction.
    onReasoningChunk: (chunk: string) => {
      if (!live.description) live.description = excerptReasoning(chunk);
      forThisSession(() => useBotStore.getState().setReasoningAsActionDescription(chunk))();
    },
    onActionDescriptionStart: () => {
      live.description = "";
      forThisSession(() => useBotStore.getState().startActionDescription())();
    },
    onActionDescriptionChunk: (chunk: string) => {
      live.description = (live.description ?? "") + chunk;
      forThisSession(() => useBotStore.getState().appendActionDescription(chunk))();
    },
    // Nothing to record: `endActionDescription` is a no-op in the store.
    onActionDescriptionEnd: forThisSession(() => useBotStore.getState().endActionDescription()),
  };
};

// ---------------------------------------------------------------------------
// Store-aware entry points — what apps and `@noa/ui` call
// ---------------------------------------------------------------------------

/**
 * Runs one stream with session-scoped handlers, dropping what it recorded however it settles —
 * including on error, so a failed stream can't leave a stale bubble or status for a later visit to
 * restore.
 */
const runStream = async (sessionId: string, stream: (handlers: ChatStreamHandlers) => Promise<void>): Promise<void> => {
  try {
    await stream(createStoreStreamHandlers(sessionId));
  } finally {
    liveStreams.delete(sessionId);
  }
};

/** Fetches bot name + version into `useBotStore`. Call once at app start. */
export const initChatbotMetadata = async () => {
  const { setBotName, setVersion } = useBotStore.getState();

  try {
    const { botName, version } = await fetchChatbotMetadata();
    setBotName(botName);
    setVersion(version);
  } catch (e: unknown) {
    console.error("[initChatbotMetadata] Failed to fetch bot metadata:", e);
  }
};

/**
 * Messages sent before the session exists — while a scaled-to-zero API is still starting, which
 * takes over a minute. Their bubbles are already in the store (the composer adds them); this keeps
 * the text, so `initConversation` can send them once it has a session to send them to.
 */
const heldMessages: string[] = [];

/** Forgets the held messages — a fresh start must not send what was typed into an older one. */
export const discardHeldMessages = (): void => {
  heldMessages.length = 0;
};

/**
 * Creates a fresh session, publishes its id to the store, then streams the greeting — or, when
 * messages were held while waiting for that session, sends them in order instead: the server refuses
 * to greet a session that already holds messages. The id is set before streaming so the first chunk
 * lands in the right conversation.
 */
export const initConversation = async () => {
  const { setSessionId } = useAppStore.getState();

  try {
    const sessionId = await createConversationSession();
    const held = heldMessages.splice(0);
    setSessionId(sessionId);

    if (held.length === 0) {
      await runStream(sessionId, (handlers) => streamGreeting(sessionId, handlers));
      return;
    }
    for (const message of held) await sendMessage(message);
  } catch (e: unknown) {
    console.error("[initConversation] Failed to initialize stream:", e);
    reportError(e);
  }
};

/** Resolves with the active session id — at once if there is one, otherwise when one is published. */
export const whenSessionReady = (): Promise<string> =>
  new Promise((resolve) => {
    const { sessionId } = useAppStore.getState();
    if (sessionId) {
      resolve(sessionId);
      return;
    }

    const unsubscribe = useAppStore.subscribe((state) => {
      if (!state.sessionId) return;
      unsubscribe();
      resolve(state.sessionId);
    });
  });

/**
 * Streams the assistant's reply to `message` into the active session's store, or holds it while
 * there is no session yet (see `heldMessages`).
 */
export const sendMessage = async (message: string) => {
  const { sessionId } = useAppStore.getState();
  if (!sessionId) {
    heldMessages.push(message);
    return;
  }

  try {
    await runStream(sessionId, (handlers) => streamMessage(sessionId, message, handlers));
  } catch (e: unknown) {
    console.error("[sendMessage] Failed to send message:", e);
    reportError(e);
  }
};
